/**
 * The useTeamCan composable is used to check and see if the current user has permission to access a particular feature in
 * the teams app. This composable also has a sister TeamCan.vue component that can be used to check permission in
 * templates
 *
 * This composable works by using the features array found in the organization and teams data sent by the backend.
 * each features array contain an array of strings that represent actions that you are allowed to take within
 * the tinyurl application. for example, you might see a string called "create_tinyurl" that as you may
 * guess, means that you have permission to create a tinyurl. If the "create_tinyurl" string is not
 * present in the features array, this means that you do not have permission to create a new url.
 *
 * The organization features/permissions always supercede the teams permissions so if you have the "create_tinyurl"
 * string in the organization's features array, but it is not present in the team array, you still have
 * permission to create a url.
 *
 * USAGE:
 * Pass the composable an organization and/or team wrapped in a vue ref. You don't need to pass both but you do need to
 * pass at least one of the two. The composable will return a helper method called
 * can(name:string):boolean
 * that can be used to check and see if you have permission to perform a particular action.
 *
 * ******************
 * Example 1
 *
 * // note that these are actually vue refs
 * organization = { features: ["create_tinyurl"]}, team = {features: []}
 * const { can } = teamCan({ org: organization, team: team })
 *
 * // returns true because the org has the feature
 * can("create_tinyurl")
 *
 * *******************
 * Example 2
 * organization = { features: []}, team = {features: ["create_tinyurl"]}
 * // returns true because the team has the feature
 * can("create_tinyurl")
 *
 * ********************
 * Example 3
 * organization = { features: []}, team = {features: []}
 * // returns false because neither the team or org has this feature
 * can("create_tinyurl")
 *
 *
 * ------------------------
 * Running a callback if you have permission
 * -----------------------
 *
 * The can method accepts an optional callback method that will be called only if you have permission to perform
 * the action.
 *
 * can("create_tinyurl", () => {
 *   console.log("this will only get called if we have permission to create a url");
 * });
 */
import {computed, ComputedRef, isRef, ref} from "vue";
import {OrganizationData, TeamData} from "../Data/Models/OrgTypes";
import type {Feature} from "../../../laravel.d.ts";
import {ChecksPermissions} from "../../shared/interfaces/ChecksPermissions.ts";

export type TeamFeature = Feature |
    'create_team' |
    'archive_team' |
    'create_organization' |
    'archive_organization' |
    'update_organization' |
    'view_org_audit' |
    'view_org_settings' |
    'view_team_settings' |
    'add_org_user' |
    'manage_org_roles' |
    'manage_org_domains' |
    'manage_org_sso' |
    'manage_subscriptions' |
    (string & {})


export default function useTeamCan({
    org = ref<OrganizationData | null>(null),
    team = ref<TeamData | null>(null)
}
): ChecksPermissions<TeamFeature> & {
    orgPermissions: ComputedRef<string[]>,
    teamPermissions: ComputedRef<string[]>,
    combinedFeatures: ComputedRef<Record<string, number>>
} {
    if (!isRef(org)) {
        throw new Error("org passed to isTeamCan is not a ref");
    }

    if (!isRef(team)) {
        throw new Error("team passed to isTeamCan is not a ref");
    }

    const orgPermissions = computed<string[]>(() => {
        return org?.value?.features || [];
    });

    const teamPermissions = computed<string[]>(() => {
        return team?.value?.features || [];
    });

    const combinedFeatures = computed<Record<string, number>>(() => {
        const retval: Record<string, number> = {};
        Object.values([...orgPermissions.value, ...teamPermissions.value]).map((item) => retval[item] = 1);
        return retval;
    });

    const can = (payload: TeamFeature | TeamFeature[], onSuccess = () => {
    }): boolean => {
        const retval = Array.isArray(payload)
          ? payload.every((feature) => !!combinedFeatures?.value?.[feature])
          : !!combinedFeatures?.value?.[payload];

        if (retval) {
            onSuccess();
        }

        return retval;
    };

    return {
        orgPermissions,
        teamPermissions,
        combinedFeatures,
        can,
    }
};
